import {type GameSettings, RoomSettingConstants} from '@colonist/game-base-types'
import {type RoomSessionClientState} from './RoomSessionClientState.ts'
import {type BigId, type NonFunctionProperties} from '@colonist/types'
import {RoomCreationPhase} from '@colonist/lobby-types'

type RoomGameSettings = Omit<NonFunctionProperties<GameSettings>, 'gameType' | 'channelId' | 'eloType' | 'extensionSetting' | 'scenarioSetting' | 'karmaActive'>

export class RoomClientState {
    gameSetting: RoomGameSettings

    isTournament: boolean

    hostSession: RoomSessionClientState
    guests: RoomSessionClientState[]

    isTestFreeExpansionsAndMaps: boolean
    creationPhase: RoomCreationPhase = RoomCreationPhase.EditSettings
    victoryPointsRecommendedLimit: number = RoomSettingConstants.DEFAULT_VP_TO_WIN
    victoryPointsMaxAllowed: number

    kickedUserIds: BigId[]

    updateSequence: number

    static deserialize(data: NonFunctionProperties<RoomClientState>): RoomClientState {
        const room = new RoomClientState()
        room.updateSequence = data.updateSequence
        room.gameSetting = data.gameSetting
        room.isTournament = data.isTournament
        room.hostSession = data.hostSession
        room.isTestFreeExpansionsAndMaps = data.isTestFreeExpansionsAndMaps
        room.creationPhase = data.creationPhase
        room.guests = data.guests
        room.victoryPointsRecommendedLimit = data.victoryPointsRecommendedLimit
        room.victoryPointsMaxAllowed = data.victoryPointsMaxAllowed
        room.kickedUserIds = [...data.kickedUserIds]
        return room
    }

    getSessionCount(): number {
        return this.guests.length + 1
    }

    getSessions(): RoomSessionClientState[] {
        return [
            ...this.guests,
            this.hostSession,
        ]
    }

    getGuestSessions(): RoomSessionClientState[] {
        return [...this.guests]
    }

    getUsernames(): string[] {
        return this.getSessions().map((session) => session.username)
    }

    getUserIds(): BigId[] {
        const userIds = this.getSessions().map((session) => session.userId)
        const filteredUserIds = userIds.filter((userId: BigId | undefined) => userId != undefined)
        return filteredUserIds as BigId[]
    }

    isInRoom(session: RoomSessionClientState): boolean {
        for(const sessionInRoom of this.getSessions()) {
            if(sessionInRoom.userSessionId == session.userSessionId) return true
        }
        return false
    }

    isMultiplayer(): boolean {
        for(const session of this.getGuestSessions()) {
            if(!session.isBot) return true
        }
        return false
    }

    isHost(id: string): boolean {
        return id == this.hostSession.userSessionId
    }

    isHostSubscribed(): boolean {
        return this.hostSession.isMember
    }

    isGuest(id: string): boolean {
        return id != this.hostSession.userSessionId
    }

    isPrivateGame(): boolean {
        return this.gameSetting.privateGame
    }

    isNoTrollsGame(): boolean {
        return this.gameSetting.minimumKarma == RoomSettingConstants.NO_TROLLS_KARMA_THRESHOLD
    }
}
